import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ServiceCreationStepProps {
  onComplete: () => void;
  onCancel: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

interface AutoGeneratedService {
  name: string;
  description: string;
  price: number;
  isComplete: boolean;
  isLoading: boolean;
  error?: string;
}

export const ServiceCreationStep = ({ onComplete, onCancel, isLoading, setIsLoading }: ServiceCreationStepProps) => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [userProfession, setUserProfession] = useState<string | null>(null);
  const [userCountry, setUserCountry] = useState<string>("Kenya");
  const [userCurrency, setUserCurrency] = useState("KES");
  const [isGeneratingServices, setIsGeneratingServices] = useState(true);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [generatedServices, setGeneratedServices] = useState<AutoGeneratedService[]>([
    {
      name: "",
      description: "",
      price: 0,
      isComplete: false,
      isLoading: true
    },
    {
      name: "",
      description: "",
      price: 0,
      isComplete: false,
      isLoading: true
    }
  ]);
  const serviceCreationStarted = useRef(false);

  // Format price for display
  const formatPrice = (price: number) => {
    return `${userCurrency} ${price.toLocaleString()}`;
  };
  
  // Load user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('profession, country')
            .eq('id', user.id)
            .single();
            
          if (error) {
            console.error("Error fetching profile:", error);
            return;
          }
          
          if (profile.profession) {
            setUserProfession(profile.profession);
          }
          
          if (profile.country) {
            setUserCountry(profile.country);
          }
          
          // Set currency based on country
          if (profile.country === "Ethiopia") {
            setUserCurrency("ETB");
          } else {
            setUserCurrency("KES");
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    
    fetchUserProfile();
  }, []);

  // Auto-generate services once we have the user's profession
  useEffect(() => {
    if (userProfession && !serviceCreationStarted.current) {
      serviceCreationStarted.current = true;
      generateServices();
    }
  }, [userProfession]);

  const generateServices = async () => {
    setIsGeneratingServices(true);
    
    try {
      // Get the user's profile to retrieve their currency
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('Error fetching user:', userError);
        throw new Error('Authentication error');
      }
      
      // Get the user's profile to access their currency
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('profession, currency')
        .eq('id', user.id)
        .single();
      
      if (profileError) {
        console.error('Error fetching profile:', profileError);
        throw new Error('Failed to fetch user profile');
      }
      
      const userCurrency = profileData.currency || 'KES'; // Default to KES if no currency set
      
      console.log(`Generating services for ${userProfession} with currency: ${userCurrency}`);
      
      // Generate service suggestions based on profession and currency
      const { data, error } = await supabase.functions.invoke('generate-service-suggestions', {
        body: { 
          profession: userProfession,
          currency: userCurrency,
          locale: i18n.language
        }
      });

      if (error) {
        console.error('Error fetching service suggestions:', error);
        toast({
          variant: "destructive",
          title: t('onboarding.error'),
          description: t('onboarding.errorGeneratingServices'),
        });
        return;
      }

      if (data && data.services && data.services.length >= 2) {
        // Select the first two services
        const selectedServices = data.services.slice(0, 2);
        
        // Update state with the generated services
        setGeneratedServices([
          {
            name: selectedServices[0].name,
            description: selectedServices[0].description,
            price: selectedServices[0].price,
            isComplete: false,
            isLoading: false
          },
          {
            name: selectedServices[1].name,
            description: selectedServices[1].description,
            price: selectedServices[1].price,
            isComplete: false,
            isLoading: false
          }
        ]);
        
        // Create the services one after another
        await createService(0, selectedServices[0]);
        await createService(1, selectedServices[1]);
        
        // Show success dialog
        setShowSuccessDialog(true);
      } else {
        throw new Error("Failed to generate enough service suggestions");
      }
    } catch (error) {
      console.error('Error generating services:', error);
      toast({
        variant: "destructive",
        title: t('onboarding.error'),
        description: t('onboarding.errorCreatingServices'),
      });
    } finally {
      setIsGeneratingServices(false);
    }
  };
  
  const createService = async (index: number, service: any) => {
    try {
      // First update state to show this service is loading
      setGeneratedServices(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          isLoading: true
        };
        return updated;
      });
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error("Authentication error. Please try again.");
      }
      
      // Create service record
      const { data, error } = await supabase
        .from('services')
        .insert({
          user_id: user.id,
          name: service.name,
          description: service.description,
          price: service.price,
          is_active: true,
          service_mode: "in_person", // Default value
          instant_booking: false, // Default value
          requires_scheduling: true, // Default value
        })
        .select()
        .single();
        
      if (error) {
        throw error;
      }
      
      // Update state to show this service is complete
      setGeneratedServices(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          isComplete: true,
          isLoading: false
        };
        return updated;
      });
      
      return data;
    } catch (error) {
      console.error(`Error creating service ${index + 1}:`, error);
      
      // Update state to show error
      setGeneratedServices(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          isLoading: false,
          error: error instanceof Error ? error.message : "An unknown error occurred"
        };
        return updated;
      });
      
      return null;
    }
  };

  return (
    <div>
      <div className="text-center mb-4">
        <p className="text-lg font-medium">{t('onboarding.step2of2')}</p>
      </div>
      
      <h2 className="text-2xl font-bold mb-6 text-center bg-gradient-to-r from-gebeya-pink to-gebeya-orange bg-clip-text text-transparent">
        {t('onboarding.creatingYourInitialServices')}
      </h2>
      
      <div className="space-y-8 mb-8">
        <p className="text-center text-gray-700 mb-6">
          {t('onboarding.generatingServicesDescription', { profession: userProfession || "business" })}
        </p>
        
        {isGeneratingServices && (
          <div className="flex flex-col items-center justify-center py-4">
            <div className="relative w-16 h-16 mb-4">
              <RefreshCw 
                className="w-16 h-16 text-gebeya-pink animate-spin"
                style={{ 
                  backgroundImage: 'linear-gradient(to right, #FF1493, #FFA500)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}
              />
            </div>
            <p className="text-gray-600 mb-4">{t('onboarding.generatingServicesProgress')}</p>
            <div className="w-full max-w-md">
              <Progress value={65} className="h-1.5 bg-gray-200" 
                style={{ 
                  '--progress-background': 'linear-gradient(to right, #FF1493, #FFA500)'
                } as React.CSSProperties} 
              />
            </div>
          </div>
        )}
        
        {!isGeneratingServices && (
          <div className="space-y-6">
            {generatedServices.map((service, i) => (
              <div key={i} className="border rounded-lg p-4 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {service.isLoading ? (
                      <RefreshCw className="w-5 h-5 text-gebeya-pink animate-spin" />
                    ) : service.isComplete ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : service.error ? (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <div className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-grow">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium text-gray-900">{service.name}</h3>
                      <span className="text-gebeya-pink font-medium">
                        {formatPrice(service.price)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{service.description}</p>
                    
                    {service.error && (
                      <Alert variant="destructive" className="mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{t('onboarding.error')}</AlertTitle>
                        <AlertDescription>
                          {service.error}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading || isGeneratingServices}
          >
            {t('onboarding.cancel')}
          </Button>
          
          <Button
            type="button"
            disabled={
              isLoading || 
              isGeneratingServices || 
              !generatedServices.some(s => s.isComplete)
            }
            className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:opacity-90"
            onClick={onComplete}
          >
            {isLoading || isGeneratingServices ? t('onboarding.creatingServices') : t('onboarding.editMyServices')}
          </Button>
        </div>
      </div>
    </div>
  );
}; 